import { useEffect, type ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";
import { AppShell as AppShellLayout, cn } from "@nexus/ui";
import { TopNav } from "@/components/layout/TopNav";
import { RetoolEmbed } from "@/features/workspace/components/RetoolEmbed";
import { useAuth } from "@/features/auth/hooks/useAuth";
import type { TenantPermissions } from "@/features/auth/lib/api";

interface AppShellProps {
  children: ReactNode;
  tenants?: TenantPermissions[];
}

/** Applied to `documentElement` while AppShell mounts — pairs with globals.css scroll lock. */
const APP_SHELL_SCROLL_LOCK_CLASS = "nexus-app-shell";

export function AppShell({ children, tenants }: AppShellProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const isWorkspace = pathname === "/workspace";

  useEffect(() => {
    document.documentElement.classList.add(APP_SHELL_SCROLL_LOCK_CLASS);
    return () => document.documentElement.classList.remove(APP_SHELL_SCROLL_LOCK_CLASS);
  }, []);

  return (
    <AppShellLayout header={<TopNav tenants={tenants} />}>
      <RetoolEmbed visible={isWorkspace} tenantId={activeTenantId} />
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden",
          isWorkspace && "hidden",
        )}
      >
        {children}
      </div>
    </AppShellLayout>
  );
}
